/**
 * Keyboard Shortcuts System
 *
 * Provides global keyboard shortcuts with a help dialog.
 * Integrates with the application for navigation, search, and common actions.
 *
 * Shortcuts:
 * - Cmd/Ctrl + K: Open global search
 * - Cmd/Ctrl + /: Show shortcuts help
 * - Escape: Close dialogs/clear selection
 * - g then h: Go to dashboard (home)
 * - g then s: Go to students
 * - g then f: Go to families
 * - g then c: Go to classes
 * - g then i: Go to invoices
 * - g then p: Go to payments
 * - g then e: Go to enrollments
 * - g then r: Go to reports
 *
 * Each "g" chord is offered (and listed in the help dialog) only to people
 * whose role can open its page; "g h" goes to the person's own dashboard.
 */

import React, { createContext, useContext, useState, useCallback, useEffect, useMemo, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  IconButton,
  Divider,
  Chip,
  useTheme,
  alpha,
} from "@mui/material";
import { Close as CloseIcon, Keyboard as KeyboardIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/store/authStore";
import { homePathForRole } from "@/utils/homePath";
import { NAV_CHORDS, resolveChordPath } from "@/utils/navChords";

/** Keyboard shortcut definition */
interface Shortcut {
  /** Unique key for the shortcut */
  id: string;
  /** Display label */
  label: string;
  /** Key combination (e.g., "mod+k", "g h") */
  keys: string;
  /** Shortcut category */
  category: "navigation" | "actions" | "general";
  /** Action to perform */
  action: () => void;
  /** Whether shortcut is enabled */
  enabled?: boolean;
}

/**
 * Is focus somewhere a letter key means something else — a native select or
 * an OPEN MUI menu / listbox, whose type-ahead jumps to the item starting
 * with that letter? A closed MUI Select (a combobox that isn't expanded) is
 * just a focused button, and the chords work there.
 */
const isInPicker = (target: HTMLElement): boolean =>
  target.tagName === "SELECT" ||
  !!target.closest?.(
    '[role="listbox"], [role="menu"], [role="combobox"][aria-expanded="true"]',
  );

interface KeyboardShortcutsContextValue {
  /** Open the shortcuts help dialog */
  openHelp: () => void;
  /** Close the shortcuts help dialog */
  closeHelp: () => void;
  /** Whether help dialog is open */
  isHelpOpen: boolean;
  /** Register a custom shortcut */
  registerShortcut: (shortcut: Shortcut) => () => void;
  /** Unregister a shortcut */
  unregisterShortcut: (id: string) => void;
}

const KeyboardShortcutsContext = createContext<KeyboardShortcutsContextValue | null>(null);

export const useKeyboardShortcuts = () => {
  const context = useContext(KeyboardShortcutsContext);
  if (!context) {
    throw new Error("useKeyboardShortcuts must be used within KeyboardShortcutsProvider");
  }
  return context;
};

/** Check if the current platform is Mac */
const isMac = typeof navigator !== "undefined" && /Mac|iPod|iPhone|iPad/.test(navigator.platform);

/** Get the modifier key label based on platform */
const getModKey = () => (isMac ? "⌘" : "Ctrl");

/** Format shortcut keys for display */
const formatKeys = (keys: string): string[] => {
  return keys.split(" ").map((key) => {
    return key
      .replace("mod", getModKey())
      .replace("shift", "⇧")
      .replace("alt", isMac ? "⌥" : "Alt")
      .replace("ctrl", "Ctrl")
      .toUpperCase();
  });
};

interface KeyboardShortcutsProviderProps {
  children: React.ReactNode;
  /** Callback for global search (Cmd+K) */
  onSearch?: () => void;
}

export const KeyboardShortcutsProvider: React.FC<KeyboardShortcutsProviderProps> = ({
  children,
  onSearch,
}) => {
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.user?.role);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [customShortcuts, setCustomShortcuts] = useState<Shortcut[]>([]);
  const gPressedRef = useRef(false);
  const gTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Read by the key handler through a ref, so that opening or closing the
  // help dialog does not re-subscribe the handler (see the effect below).
  const isHelpOpenRef = useRef(isHelpOpen);
  useEffect(() => {
    isHelpOpenRef.current = isHelpOpen;
  }, [isHelpOpen]);

  // Built-in shortcuts. Memoised: the key handler's effect depends on the
  // list, and a fresh array on every render re-subscribed the handler on
  // every render -- whose cleanup cleared the "g" chord's one-second timer
  // and left the chord armed until the next keypress (change review
  // 2026-09-15, portion 5).
  const builtInShortcuts = useMemo<Shortcut[]>(() => [
    // General
    {
      id: "search",
      label: "Open search",
      keys: "mod+k",
      category: "general",
      action: () => onSearch?.(),
      enabled: !!onSearch,
    },
    {
      id: "help",
      label: "Show keyboard shortcuts",
      keys: "mod+/",
      category: "general",
      action: () => setIsHelpOpen(true),
    },
    {
      id: "close",
      label: "Close dialog / Clear selection",
      keys: "esc",
      category: "general",
      action: () => {
        // This is handled by individual components
      },
    },

    // Navigation (g + key). Home is wherever this person's dashboard is.
    {
      id: "go-home",
      label: "Go to Home",
      keys: "g h",
      category: "navigation",
      action: () => navigate(homePathForRole(role)),
      enabled: !!role,
    },
    ...NAV_CHORDS.map((chord): Shortcut => {
      const path = resolveChordPath(chord, role);
      return {
        id: chord.id,
        label: chord.label,
        keys: `g ${chord.key}`,
        category: "navigation",
        action: () => {
          if (path) navigate(path);
        },
        enabled: path !== null,
      };
    }),
  ], [navigate, onSearch, role]);

  const allShortcuts = useMemo(
    () => [...builtInShortcuts, ...customShortcuts].filter((s) => s.enabled !== false),
    [builtInShortcuts, customShortcuts]
  );

  const openHelp = useCallback(() => setIsHelpOpen(true), []);
  const closeHelp = useCallback(() => setIsHelpOpen(false), []);

  const registerShortcut = useCallback((shortcut: Shortcut) => {
    setCustomShortcuts((prev) => [...prev.filter((s) => s.id !== shortcut.id), shortcut]);
    return () => {
      setCustomShortcuts((prev) => prev.filter((s) => s.id !== shortcut.id));
    };
  }, []);

  const unregisterShortcut = useCallback((id: string) => {
    setCustomShortcuts((prev) => prev.filter((s) => s.id !== id));
  }, []);

  // Handle keyboard events
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement;
      const isTyping =
        target.tagName === "INPUT" ||
        target.tagName === "TEXTAREA" ||
        target.isContentEditable ||
        isInPicker(target);

      const modKey = isMac ? e.metaKey : e.ctrlKey;

      // Search comes FIRST, before the typing guard.
      //
      // Someone half-way through a filter box who remembers a different
      // student is the palette's most common caller. A modified key is never
      // text input, so there is nothing to protect here.
      // Case-insensitive: with Caps Lock on, the key reports "K", and the
      // palette must still open.
      if (modKey && e.key.toLowerCase() === "k") {
        e.preventDefault();
        onSearch?.();
        return;
      }

      // Help, for the same reason.
      if (modKey && e.key === "/") {
        e.preventDefault();
        setIsHelpOpen(true);
        return;
      }

      // Everything below is a bare or single-letter shortcut, which would
      // otherwise eat what someone is typing. Escape still gets through.
      if (isTyping && e.key !== "Escape") {
        return;
      }

      // Handle Escape (close help)
      if (e.key === "Escape") {
        if (isHelpOpenRef.current) {
          setIsHelpOpen(false);
        }
        return;
      }

      // Case-insensitive throughout: with Caps Lock on (or Shift held) the
      // keys report "G" and "S", and the chord must still work. Ctrl, Cmd and
      // Alt presses are other shortcuts (Ctrl+Shift+R is reload), never part
      // of a chord.
      const key = e.key.toLowerCase();
      const otherModifier = e.ctrlKey || e.metaKey || e.altKey;

      // Handle "g" prefix for navigation
      if (key === "g" && !otherModifier) {
        gPressedRef.current = true;
        // Reset after 1 second
        if (gTimeoutRef.current) {
          clearTimeout(gTimeoutRef.current);
        }
        gTimeoutRef.current = setTimeout(() => {
          gPressedRef.current = false;
        }, 1000);
        return;
      }

      // Handle navigation shortcuts (g + key)
      if (gPressedRef.current && !otherModifier) {
        const navShortcut = allShortcuts.find(
          (s) => s.category === "navigation" && s.keys === `g ${key}`
        );
        if (navShortcut) {
          e.preventDefault();
          navShortcut.action();
          gPressedRef.current = false;
          if (gTimeoutRef.current) {
            clearTimeout(gTimeoutRef.current);
          }
        }
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      // A chord cannot outlive its handler.
      gPressedRef.current = false;
      if (gTimeoutRef.current) {
        clearTimeout(gTimeoutRef.current);
      }
    };
  }, [allShortcuts, onSearch]);

  const contextValue: KeyboardShortcutsContextValue = {
    openHelp,
    closeHelp,
    isHelpOpen,
    registerShortcut,
    unregisterShortcut,
  };

  return (
    <KeyboardShortcutsContext.Provider value={contextValue}>
      {children}
      <ShortcutsHelpDialog
        open={isHelpOpen}
        onClose={closeHelp}
        shortcuts={allShortcuts}
      />
    </KeyboardShortcutsContext.Provider>
  );
};

interface ShortcutsHelpDialogProps {
  open: boolean;
  onClose: () => void;
  shortcuts: Shortcut[];
}

const ShortcutsHelpDialog: React.FC<ShortcutsHelpDialogProps> = ({
  open,
  onClose,
  shortcuts,
}) => {
  const theme = useTheme();

  const categories = [
    { key: "general", label: "General" },
    { key: "navigation", label: "Navigation" },
    { key: "actions", label: "Actions" },
  ] as const;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      slotProps={{
        paper: {
          sx: { maxHeight: "80vh" },
        }
      }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        <KeyboardIcon />
        <Typography variant="h6" component="span" sx={{ flexGrow: 1 }}>
          Keyboard Shortcuts
        </Typography>
        <IconButton size="small" onClick={onClose} aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {categories.map((category, categoryIndex) => {
          const categoryShortcuts = shortcuts.filter(
            (s) => s.category === category.key
          );
          if (categoryShortcuts.length === 0) return null;

          return (
            <Box key={category.key} sx={{ mb: categoryIndex < categories.length - 1 ? 3 : 0 }}>
              <Typography
                variant="overline"
                sx={{
                  color: "text.secondary",
                  mb: 1,
                  display: "block"
                }}>
                {category.label}
              </Typography>
              {categoryShortcuts.map((shortcut) => (
                <Box
                  key={shortcut.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    py: 1,
                    px: 1,
                    borderRadius: 1,
                    "&:hover": {
                      bgcolor: alpha(theme.palette.primary.main, 0.04),
                    },
                  }}
                >
                  <Typography variant="body2">{shortcut.label}</Typography>
                  <Box sx={{ display: "flex", gap: 0.5 }}>
                    {formatKeys(shortcut.keys).map((key, index) => (
                      <React.Fragment key={index}>
                        {index > 0 && (
                          <Typography
                            variant="caption"
                            sx={{
                              color: "text.secondary",
                              mx: 0.5
                            }}>
                            then
                          </Typography>
                        )}
                        <Chip
                          label={key}
                          size="small"
                          sx={{
                            fontFamily: "monospace",
                            fontWeight: 500,
                            minWidth: 32,
                            height: 24,
                            bgcolor: alpha(theme.palette.text.primary, 0.08),
                          }}
                        />
                      </React.Fragment>
                    ))}
                  </Box>
                </Box>
              ))}
              {categoryIndex < categories.length - 1 && <Divider sx={{ mt: 2 }} />}
            </Box>
          );
        })}
      </DialogContent>
    </Dialog>
  );
};

/**
 * Button to open keyboard shortcuts help
 */
export const KeyboardShortcutsButton: React.FC = () => {
  const { openHelp } = useKeyboardShortcuts();

  return (
    <IconButton
      onClick={openHelp}
      size="small"
      aria-label="Keyboard shortcuts"
      title={`Keyboard shortcuts (${getModKey()}+/)`}
    >
      <KeyboardIcon />
    </IconButton>
  );
};

export default KeyboardShortcutsProvider;
